import { useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHeader, ErrorState, StatusPill } from '@ui-kit';
import { useRoleDetail } from '../hooks/useRoleDetail';
import { useUpdateRole } from '../hooks/useUpdateRole';
import { useDeleteRole } from '../hooks/useDeleteRole';
import { useUpdateRolePermissions } from '../hooks/useUpdateRolePermissions';
import { usePermissionCatalog } from '../hooks/usePermissionCatalog';
import { SystemRoleBadge } from '../components/SystemRoleBadge';
import { RoleDeleteConfirm } from '../components/RoleDeleteConfirm';
import { PermissionMatrixTable } from '../components/PermissionMatrixTable';
import type { ApiError } from '../types/role';
import { ButtonGuard } from '../../_shared/components/ButtonGuard';
import { useHasPermission } from '../../_shared/hooks/useCurrentUser';

function formatDate(isoString: string | null | undefined): string {
  if (!isoString) return '-';
  return new Date(isoString).toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** 5행 Skeleton 렌더러 */
function DetailSkeleton() {
  return (
    <div style={{ padding: 'var(--spacing-6)' }} aria-busy="true" aria-label="로딩 중">
      <div
        style={{
          height: 28,
          width: 200,
          borderRadius: 'var(--radius-sm)',
          background: 'var(--color-surface-elevated)',
          animation: 'pulse 1.5s ease-in-out infinite',
          marginBottom: 'var(--spacing-6)',
        }}
      />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 'var(--spacing-4)',
          marginBottom: 'var(--spacing-6)',
        }}
      >
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
            <div
              style={{
                height: 12,
                width: 80,
                borderRadius: 'var(--radius-sm)',
                background: 'var(--color-surface-elevated)',
                animation: 'pulse 1.5s ease-in-out infinite',
              }}
            />
            <div
              style={{
                height: 16,
                width: '100%',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--color-surface-elevated)',
                animation: 'pulse 1.5s ease-in-out infinite',
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export function RoleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const roleId = id ? parseInt(id, 10) : undefined;

  const detailQuery = useRoleDetail(roleId);
  const catalogQuery = usePermissionCatalog();
  const role = detailQuery.data;

  const updateMutation = useUpdateRole(roleId ?? 0);
  const deleteMutation = useDeleteRole();
  const updatePermsMutation = useUpdateRolePermissions(roleId ?? 0);

  // RBAC: ROLE_WRITE 보유 여부 — ButtonGuard 와 동일한 훅 재사용
  const canEdit = useHasPermission('ROLE_WRITE');

  // 편집 상태
  const [editMode, setEditMode] = useState(false);
  const [nameKo, setNameKo] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<number | ''>('');
  const [updateError, setUpdateError] = useState<string | undefined>();
  const [successMessage, setSuccessMessage] = useState<string | undefined>();

  // 삭제 다이얼로그 상태
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | undefined>();
  const [deleteErrorCode, setDeleteErrorCode] = useState<string | undefined>();

  // 매트릭스 편집 상태
  // pendingAdd/pendingRemove: 카탈로그 코드 기준으로 추가/제거 예정 목록
  const [matrixPendingAdd, setMatrixPendingAdd] = useState<Set<string>>(new Set());
  const [matrixPendingRemove, setMatrixPendingRemove] = useState<Set<string>>(new Set());
  const [matrixError, setMatrixError] = useState<string | undefined>();
  const [matrixSuccess, setMatrixSuccess] = useState<string | undefined>();

  // 현재 부여된 permission 코드 집합
  const grantedCodes = useMemo(
    () => new Set((role?.permissions ?? []).map((p) => p.code)),
    [role?.permissions]
  );

  // 편집 시작
  const handleStartEdit = useCallback(() => {
    if (!role) return;
    setNameKo(role.nameKo);
    setNameEn(role.nameEn ?? '');
    setDescription(role.description ?? '');
    setPriority(role.priority);
    setEditMode(true);
    setUpdateError(undefined);
  }, [role]);

  // 저장
  const handleSave = useCallback(() => {
    if (!role) return;
    setUpdateError(undefined);
    updateMutation.mutate(
      {
        nameKo: nameKo.trim() || undefined,
        nameEn: nameEn.trim() || undefined,
        description: description.trim() || undefined,
        priority: typeof priority === 'number' ? priority : undefined,
      },
      {
        onSuccess: () => {
          setEditMode(false);
          setSuccessMessage(t('role.toast.updated'));
          setTimeout(() => setSuccessMessage(undefined), 3000);
        },
        onError: (err: unknown) => {
          const apiErr = err as ApiError;
          setUpdateError(apiErr.message ?? t('role.error.network'));
        },
      }
    );
  }, [role, nameKo, nameEn, description, priority, updateMutation]);

  // 삭제
  const handleDeleteConfirm = useCallback(() => {
    if (!roleId || !role) return;
    setDeleteError(undefined);
    setDeleteErrorCode(undefined);
    const deletedCode = role.code;
    deleteMutation.mutate(roleId, {
      onSuccess: () => {
        // R9-F-005: navigate with delete success state so RoleListPage can display toast
        navigate('/roles', {
          state: { deletedRoleCode: deletedCode },
        });
      },
      onError: (err: unknown) => {
        const apiErr = err as ApiError;
        setDeleteError(apiErr.message ?? t('role.error.network'));
        setDeleteErrorCode(apiErr.code);
      },
    });
  }, [roleId, role, deleteMutation, navigate]);

  // 매트릭스 토글
  const handleMatrixToggle = useCallback(
    (code: string, currentChecked: boolean) => {
      setMatrixError(undefined);
      setMatrixPendingAdd((prev) => {
        const next = new Set(prev);
        if (currentChecked) {
          // 체크 → 미체크: pendingRemove 에 추가, pendingAdd 에서 제거
          next.delete(code);
          return next;
        } else {
          // 미체크 → 체크: pendingAdd 에 추가
          if (!grantedCodes.has(code)) {
            next.add(code);
          }
          return next;
        }
      });
      setMatrixPendingRemove((prev) => {
        const next = new Set(prev);
        if (currentChecked) {
          // 체크 → 미체크: pendingRemove 에 추가
          if (grantedCodes.has(code)) {
            next.add(code);
          }
        } else {
          // 미체크 → 체크: pendingRemove 에서 제거
          next.delete(code);
        }
        return next;
      });
    },
    [grantedCodes]
  );

  // 매트릭스 저장
  const handleSaveMatrix = useCallback(() => {
    if (!roleId) return;
    setMatrixError(undefined);

    // 최종 permissionCodes 계산: 기존 부여 + 추가 예정 - 제거 예정
    const finalCodes = new Set(grantedCodes);
    matrixPendingAdd.forEach((c) => finalCodes.add(c));
    matrixPendingRemove.forEach((c) => finalCodes.delete(c));

    updatePermsMutation.mutate(
      { permissionCodes: Array.from(finalCodes) },
      {
        onSuccess: () => {
          setMatrixPendingAdd(new Set());
          setMatrixPendingRemove(new Set());
          setMatrixSuccess(t('role.toast.permissionsUpdated'));
          setTimeout(() => setMatrixSuccess(undefined), 3000);
        },
        onError: (err: unknown) => {
          const apiErr = err as ApiError;
          // R9-C-004: 403 error code → i18n message (not raw apiErr.message which may be i18n key)
          if (apiErr.code === 'ROLE-403-002') {
            setMatrixError(t('role.error.ROLE-403-002'));
          } else if (apiErr.code === 'ROLE-403-001') {
            setMatrixError(t('role.error.ROLE-403-001'));
          } else {
            setMatrixError(apiErr.message ?? t('role.error.network'));
          }
        },
      }
    );
  }, [roleId, grantedCodes, matrixPendingAdd, matrixPendingRemove, updatePermsMutation]);

  // 매트릭스 되돌리기
  const handleResetMatrix = useCallback(() => {
    setMatrixPendingAdd(new Set());
    setMatrixPendingRemove(new Set());
    setMatrixError(undefined);
  }, []);

  // 매트릭스 행 일괄 토글 (리소스 단위 전체 선택/해제)
  const handleBulkToggle = useCallback(
    (_resource: string, codes: string[], allChecked: boolean) => {
      setMatrixError(undefined);
      if (allChecked) {
        // 현재 행 전체 체크 → 전체 해제
        setMatrixPendingAdd((prev) => {
          const next = new Set(prev);
          codes.forEach((c) => next.delete(c));
          return next;
        });
        setMatrixPendingRemove((prev) => {
          const next = new Set(prev);
          codes.forEach((c) => {
            if (grantedCodes.has(c)) next.add(c);
          });
          return next;
        });
      } else {
        // 현재 행 일부/전체 미체크 → 전체 선택
        setMatrixPendingRemove((prev) => {
          const next = new Set(prev);
          codes.forEach((c) => next.delete(c));
          return next;
        });
        setMatrixPendingAdd((prev) => {
          const next = new Set(prev);
          codes.forEach((c) => {
            if (!grantedCodes.has(c)) next.add(c);
          });
          return next;
        });
      }
    },
    [grantedCodes]
  );

  const hasPendingMatrixChanges = matrixPendingAdd.size > 0 || matrixPendingRemove.size > 0;

  const btnPrimary: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
    padding: 'var(--spacing-2) var(--spacing-4)',
    background: 'var(--color-brand-primary)',
    color: 'var(--color-text-inverse)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'var(--font-family-sans)',
  };

  const btnSecondary: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
    padding: 'var(--spacing-2) var(--spacing-4)',
    background: 'var(--color-surface-elevated)',
    color: 'var(--color-text-primary)',
    border: '1px solid var(--color-surface-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'var(--font-family-sans)',
  };

  const btnDanger: React.CSSProperties = {
    ...btnSecondary,
    color: 'var(--color-danger)',
    borderColor: 'var(--color-danger)',
  };

  const inputStyle: React.CSSProperties = {
    padding: 'var(--spacing-2) var(--spacing-3)',
    background: 'var(--color-surface-elevated)',
    border: '1px solid var(--color-surface-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-primary)',
    fontFamily: 'var(--font-family-sans)',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box' as const,
  };

  const fieldLabelStyle: React.CSSProperties = {
    fontSize: 'var(--font-size-xs)',
    fontWeight: 600,
    color: 'var(--color-text-secondary)',
    marginBottom: 'var(--spacing-1)',
  };

  const fieldValueStyle: React.CSSProperties = {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-primary)',
  };

  // Loading state
  if (detailQuery.isLoading) {
    return (
      <div style={{ padding: 'var(--spacing-6)' }}>
        <PageHeader
          title={t('role.detail.title')}
          breadcrumb={
            <span>
              {t('role.detail.breadcrumb.system', { defaultValue: '시스템' })} &gt;{' '}
              <button
                type="button"
                onClick={() => navigate('/roles')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-brand-primary)', fontSize: 'var(--font-size-sm)' }}
              >
                {t('role.list.title')}
              </button>{' '}
              &gt; {t('role.detail.loading', { defaultValue: '로딩 중...' })}
            </span>
          }
        />
        <DetailSkeleton />
      </div>
    );
  }

  // Error state
  if (detailQuery.isError) {
    return (
      <div style={{ padding: 'var(--spacing-6)' }}>
        <PageHeader
          title={t('role.detail.title')}
          breadcrumb={<span>{t('role.detail.breadcrumb.error', { defaultValue: '시스템 / 역할 관리 / 오류' })}</span>}
          actions={
            <button type="button" onClick={() => navigate('/roles')} style={btnSecondary}>
              {t('role.detail.back')}
            </button>
          }
        />
        <ErrorState
          title={t('role.error.detailLoadFailed', { defaultValue: '역할 정보를 불러오지 못했습니다' })}
          message={
            (detailQuery.error as unknown as ApiError)?.code === 'ROLE-404-001'
              ? t('role.error.ROLE-404-001')
              : ((detailQuery.error as unknown as ApiError)?.message ?? t('role.error.network'))
          }
          onRetry={() => void detailQuery.refetch()}
        />
      </div>
    );
  }

  if (!role) return null;

  const isSystemRole = role.systemRole;
  // 매트릭스 편집 가능 여부: 시스템 역할 또는 ROLE_WRITE 미보유 시 읽기 전용
  const matrixReadonly = isSystemRole || !canEdit;

  return (
    <div style={{ padding: 'var(--spacing-6)' }}>
      {/* 페이지 헤더 */}
      <PageHeader
        title={`${t('role.detail.title')} — ${role.code}`}
        subtitle={t('roleManagement.pageHeader.subtitle')}
        breadcrumb={
          <span>
            {t('role.detail.breadcrumb.system', { defaultValue: '시스템' })} &gt;{' '}
            <button
              type="button"
              onClick={() => navigate('/roles')}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--color-brand-primary)',
                fontSize: 'var(--font-size-sm)',
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              {t('role.list.title')}
            </button>{' '}
            &gt; {role.code}
          </span>
        }
        actions={
          <div style={{ display: 'flex', gap: 'var(--spacing-2)', flexWrap: 'wrap', alignItems: 'center' }}>
            {/* 읽기 전용 배지: ROLE_WRITE 미보유 사용자에게 조회 전용임을 명시 */}
            {!canEdit && (
              <span
                title={t('role.readonly.tooltip')}
                aria-label={t('role.readonly.ariaLabel')}
                style={{ display: 'inline-flex', alignItems: 'center' }}
              >
                <StatusPill tone="neutral">{t('role.readonly.badge')}</StatusPill>
              </span>
            )}
            {/* ButtonGuard: MANAGER lacks ROLE_WRITE → disabled per #324 */}
            {!editMode && !isSystemRole && (
              <ButtonGuard requirePermission="ROLE_WRITE">
                {({ disabled, reason }) => (
                  <button
                    type="button"
                    disabled={disabled}
                    title={reason}
                    onClick={() => { if (!disabled) handleStartEdit(); }}
                    style={{
                      ...btnSecondary,
                      ...(disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                    }}
                    aria-disabled={disabled}
                  >
                    {t('role.detail.action.edit')}
                  </button>
                )}
              </ButtonGuard>
            )}
            <ButtonGuard requirePermission="ROLE_WRITE">
              {({ disabled: guardDisabled, reason: guardReason }) => {
                const isDisabled = isSystemRole || guardDisabled;
                const titleText = isSystemRole
                  ? t('role.delete.system.disabled.tooltip')
                  : guardReason;
                return (
                  <button
                    type="button"
                    disabled={isDisabled}
                    title={titleText}
                    onClick={() => {
                      if (!isDisabled) {
                        setDeleteError(undefined);
                        setDeleteErrorCode(undefined);
                        setDeleteDialogOpen(true);
                      }
                    }}
                    style={{
                      ...btnDanger,
                      opacity: isDisabled ? 0.4 : 1,
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                    }}
                    aria-disabled={isDisabled}
                  >
                    {t('role.detail.action.delete')}
                  </button>
                );
              }}
            </ButtonGuard>
          </div>
        }
      />

      {/* 성공 배너 */}
      {successMessage && (
        <div
          role="status"
          aria-live="polite"
          style={{
            marginBottom: 'var(--spacing-4)',
            padding: 'var(--spacing-3)',
            background: 'color-mix(in srgb, var(--color-success) 12%, var(--color-surface-muted))',
            border: '1px solid var(--color-success)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-success)',
          }}
        >
          {successMessage}
        </div>
      )}

      {/* 읽기 전용 안내 배너 (ROLE_WRITE 미보유, 시스템 역할이 아닌 경우) */}
      {!canEdit && !isSystemRole && (
        <div
          role="note"
          style={{
            marginBottom: 'var(--spacing-4)',
            padding: 'var(--spacing-3)',
            background: 'color-mix(in srgb, var(--color-info) 8%, var(--color-surface-muted))',
            border: '1px solid var(--color-info)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-info)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-2)',
          }}
        >
          <span aria-hidden="true">🔍</span>
          {t('role.readonly.tooltip')}
        </div>
      )}

      {/* 시스템 역할 안내 배너 */}
      {isSystemRole && (
        <div
          style={{
            marginBottom: 'var(--spacing-4)',
            padding: 'var(--spacing-3)',
            background: 'color-mix(in srgb, var(--color-warning) 10%, var(--color-surface-muted))',
            border: '1px solid var(--color-warning)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-warning)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-2)',
          }}
        >
          <span aria-hidden="true">🔒</span>
          {t('role.detail.systemRole.readonlyHint')}. {t('role.detail.systemRole.matrixReadonly')}
        </div>
      )}

      {/* 역할 정보 카드 */}
      <div
        style={{
          background: 'var(--color-surface-muted)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--spacing-4)',
          marginBottom: 'var(--spacing-4)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 'var(--spacing-4)',
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: 'var(--font-size-base)',
              fontWeight: 700,
              color: 'var(--color-text-primary)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-2)',
            }}
          >
            <span style={{ fontFamily: 'var(--font-family-mono)' }}>{role.code}</span>
            <SystemRoleBadge systemRole={role.systemRole} />
          </h2>

          {editMode && (
            <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
              <button
                type="button"
                onClick={handleSave}
                disabled={updateMutation.isPending}
                style={{
                  ...btnPrimary,
                  opacity: updateMutation.isPending ? 0.7 : 1,
                }}
              >
                {updateMutation.isPending ? t('role.detail.matrix.saving') : t('role.detail.action.save')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditMode(false);
                  setUpdateError(undefined);
                }}
                style={btnSecondary}
              >
                {t('role.detail.action.cancel')}
              </button>
            </div>
          )}
        </div>

        {/* 수정 오류 */}
        {updateError && (
          <div
            role="alert"
            style={{
              marginBottom: 'var(--spacing-3)',
              padding: 'var(--spacing-3)',
              background: 'color-mix(in srgb, var(--color-danger) 10%, var(--color-surface-muted))',
              border: '1px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-danger)',
            }}
          >
            {updateError}
          </div>
        )}

        {/* 필드 그리드 */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 'var(--spacing-4)',
          }}
        >
          {/* 역할 코드 (읽기 전용) */}
          <div>
            <div style={fieldLabelStyle}>{t('role.detail.field.code')}</div>
            <div
              style={{
                ...fieldValueStyle,
                fontFamily: 'var(--font-family-mono)',
                fontSize: 'var(--font-size-xs)',
              }}
            >
              {role.code}
            </div>
          </div>

          {/* 한글 이름 */}
          <div>
            <div style={fieldLabelStyle}>{t('role.detail.field.nameKo')}</div>
            {editMode ? (
              <input
                type="text"
                value={nameKo}
                onChange={(e) => setNameKo(e.target.value)}
                style={inputStyle}
                aria-label={t('role.detail.field.nameKo')}
              />
            ) : (
              <div style={fieldValueStyle}>{role.nameKo}</div>
            )}
          </div>

          {/* 영문 이름 */}
          <div>
            <div style={fieldLabelStyle}>{t('role.detail.field.nameEn')}</div>
            {editMode ? (
              <input
                type="text"
                value={nameEn}
                onChange={(e) => setNameEn(e.target.value)}
                style={inputStyle}
                aria-label={t('role.detail.field.nameEn')}
              />
            ) : (
              <div style={fieldValueStyle}>{role.nameEn || '-'}</div>
            )}
          </div>

          {/* 우선순위 */}
          <div>
            <div style={fieldLabelStyle}>{t('role.detail.field.priority')}</div>
            {editMode ? (
              <input
                type="number"
                value={priority}
                onChange={(e) =>
                  setPriority(e.target.value === '' ? '' : Number(e.target.value))
                }
                min={1}
                max={999}
                style={inputStyle}
                aria-label={t('role.detail.field.priority')}
              />
            ) : (
              <div style={fieldValueStyle}>{role.priority}</div>
            )}
          </div>

          {/* 사용자 수 */}
          <div>
            <div style={fieldLabelStyle}>{t('role.detail.field.userCount')}</div>
            <div style={fieldValueStyle}>{t('role.detail.field.userCount.value', { count: role.userCount })}</div>
          </div>

          {/* 설명 */}
          <div style={{ gridColumn: '1 / -1' }}>
            <div style={fieldLabelStyle}>{t('role.detail.field.description')}</div>
            {editMode ? (
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }}
                aria-label={t('role.detail.field.description')}
              />
            ) : (
              <div style={{ ...fieldValueStyle, color: 'var(--color-text-secondary)' }}>
                {role.description || '-'}
              </div>
            )}
          </div>

          {/* 감사 정보 */}
          <div style={{ gridColumn: '1 / -1' }}>
            <p
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-muted)',
                fontFamily: 'var(--font-family-mono)',
                margin: 0,
              }}
            >
              {t('role.detail.audit.info', {
                createdAt: formatDate(role.createdAt),
                createdBy: role.createdBy,
                updatedAt: formatDate(role.updatedAt),
                updatedBy: role.updatedBy,
              })} · v{role.version}
            </p>
          </div>
        </div>
      </div>

      {/* Permission 매트릭스 카드 */}
      <div
        style={{
          background: 'var(--color-surface-muted)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--spacing-4)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 'var(--spacing-3)',
            flexWrap: 'wrap',
            marginBottom: 'var(--spacing-3)',
          }}
        >
          <div>
            <h2
              style={{
                margin: 0,
                fontSize: 'var(--font-size-base)',
                fontWeight: 700,
                color: 'var(--color-text-primary)',
              }}
            >
              {t('role.detail.matrix.title')}
            </h2>
            <p
              style={{
                margin: 'var(--spacing-1) 0 0',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-muted)',
              }}
            >
              {matrixReadonly
                ? t('role.detail.matrix.hintReadonly')
                : t('role.detail.matrix.hint')}
              {isSystemRole && (
                <span style={{ color: 'var(--color-info)', marginLeft: 'var(--spacing-2)' }}>
                  {t('role.detail.systemRole.matrixReadonly')}
                </span>
              )}
              {!isSystemRole && !canEdit && (
                <span style={{ color: 'var(--color-info)', marginLeft: 'var(--spacing-2)' }}>
                  {t('role.detail.matrix.hintNoWritePerm')}
                </span>
              )}
            </p>
          </div>

          {!matrixReadonly && (
            <div style={{ display: 'flex', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
              {hasPendingMatrixChanges && (
                <button type="button" onClick={handleResetMatrix} style={btnSecondary}>
                  {t('role.detail.action.resetMatrix')}
                </button>
              )}
              <button
                type="button"
                onClick={handleSaveMatrix}
                disabled={!hasPendingMatrixChanges || updatePermsMutation.isPending}
                style={{
                  ...btnPrimary,
                  opacity: !hasPendingMatrixChanges || updatePermsMutation.isPending ? 0.6 : 1,
                  cursor: !hasPendingMatrixChanges ? 'not-allowed' : 'pointer',
                }}
              >
                {updatePermsMutation.isPending ? t('role.detail.matrix.saving') : t('role.detail.action.saveMatrix')}
              </button>
            </div>
          )}
        </div>

        {/* 매트릭스 성공 */}
        {matrixSuccess && (
          <div
            role="status"
            aria-live="polite"
            style={{
              marginBottom: 'var(--spacing-3)',
              padding: 'var(--spacing-3)',
              background: 'color-mix(in srgb, var(--color-success) 10%, var(--color-surface-muted))',
              border: '1px solid var(--color-success)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-success)',
            }}
          >
            {matrixSuccess}
          </div>
        )}

        {/* 카탈로그 로딩 / 오류 */}
        {catalogQuery.isLoading && (
          <div
            style={{
              padding: 'var(--spacing-4)',
              textAlign: 'center',
              color: 'var(--color-text-muted)',
              fontSize: 'var(--font-size-sm)',
            }}
            aria-busy="true"
          >
            {t('role.detail.matrix.catalogLoading', { defaultValue: 'Permission 카탈로그 로딩 중...' })}
          </div>
        )}

        {catalogQuery.isError && (
          <ErrorState
            title={t('role.detail.matrix.catalogLoadFailed', { defaultValue: 'Permission 카탈로그를 불러오지 못했습니다' })}
            message={(catalogQuery.error as unknown as ApiError)?.message ?? t('role.error.network')}
            onRetry={() => void catalogQuery.refetch()}
          />
        )}

        {/* 매트릭스 테이블 */}
        {catalogQuery.data && (
          <PermissionMatrixTable
            catalog={catalogQuery.data}
            grantedCodes={grantedCodes}
            pendingAdd={matrixPendingAdd}
            pendingRemove={matrixPendingRemove}
            readonly={matrixReadonly}
            onToggle={matrixReadonly ? undefined : handleMatrixToggle}
            onBulkToggle={matrixReadonly ? undefined : handleBulkToggle}
            saveError={matrixError}
            onSaveErrorRetry={matrixError ? handleResetMatrix : undefined}
          />
        )}
      </div>

      {/* 삭제 확인 다이얼로그 */}
      <RoleDeleteConfirm
        open={deleteDialogOpen}
        roleCode={role.code}
        systemRole={role.systemRole}
        onClose={() => {
          setDeleteDialogOpen(false);
          setDeleteError(undefined);
          setDeleteErrorCode(undefined);
        }}
        onConfirm={handleDeleteConfirm}
        isLoading={deleteMutation.isPending}
        errorMessage={deleteError}
        errorCode={deleteErrorCode}
      />
    </div>
  );
}
